import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES } from '../i18n';

/**
 * Language switcher. Changing the language calls i18next's changeLanguage,
 * which re-renders every useTranslation() consumer and — because the
 * LanguageDetector caches to localStorage (key i18nextLng) — persists the
 * choice across reloads with no extra code.
 *
 * Two variants:
 *  - "select" (default): a compact dropdown, for a header or toolbar.
 *  - "list": full rows in each language's own script, for a settings page.
 */
export default function LanguageSwitcher({
  variant = 'select',
}: {
  variant?: 'select' | 'list';
}) {
  const { i18n } = useTranslation();
  const current = i18n.resolvedLanguage ?? i18n.language ?? 'en';

  const change = (code: string) => {
    void i18n.changeLanguage(code);
  };

  if (variant === 'list') {
    return (
      <div className="space-y-2">
        {SUPPORTED_LANGUAGES.map((lang) => {
          const selected = current === lang.code;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => change(lang.code)}
              className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition ${
                selected
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              aria-pressed={selected}
            >
              <span>
                <span className="block text-base font-semibold text-gray-900">
                  {lang.nativeName}
                </span>
                {lang.name !== lang.nativeName && (
                  <span className="block text-xs text-gray-400">
                    {lang.name}
                  </span>
                )}
              </span>
              {selected && (
                <span className="text-emerald-600" aria-hidden>
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <select
      value={current}
      onChange={(e) => change(e.target.value)}
      aria-label="Language"
      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800"
    >
      {SUPPORTED_LANGUAGES.map((lang) => (
        <option key={lang.code} value={lang.code}>
          {lang.nativeName}
        </option>
      ))}
    </select>
  );
}
